import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Badge, Button, Form, InputGroup, Alert, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaSearch, FaUserEdit, FaUserSlash, FaUserCheck, FaFilter, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { getAdminUsers, updateAdminUser } from '../../services/adminService';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const departments = React.useMemo(() => {
    const unique = new Set(
      users
        .map(u => u?.department)
        .filter(d => typeof d === 'string' && d.trim().length > 0)
    );
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [users]);
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'dateJoined', direction: 'desc' });
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    role: '',
    department: '',
    status: ''
  });
  
  // Fetch users data
  useEffect(() => {
    let mounted = true;

    const fetchUsers = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getAdminUsers();
        if (!mounted) return;
        setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        if (!mounted) return;
        setError(err?.message || 'Failed to load users');
        setUsers([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchUsers();
    return () => {
      mounted = false;
    };
  }, []);
  
  // Handle sort
  const requestSort = (key) => {
    let direction = 'asc';
    
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key, direction });
  };
  
  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <FaSort className="ms-1 text-muted" />;
    }
    
    return sortConfig.direction === 'asc' ? 
      <FaSortUp className="ms-1 text-primary" /> : 
      <FaSortDown className="ms-1 text-primary" />;
  };
  
  // Sort and filter users
  const filteredUsers = React.useMemo(() => {
    let filtered = [...users];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply role filter
    if (roleFilter) {
      filtered = filtered.filter(user => user.role === roleFilter);
    }
    
    // Apply department filter
    if (departmentFilter) {
      filtered = filtered.filter(user => user.department === departmentFilter);
    }
    
    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(user => user.status === statusFilter);
    }
    
    // Apply sorting
    return filtered.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [users, searchTerm, roleFilter, departmentFilter, statusFilter, sortConfig]);
  
  // Handle user status toggle
  const handleToggleStatus = async (userId) => {
    try {
      setError('');
      const target = users.find(u => String(u.id) === String(userId));
      if (!target) throw new Error('User not found');
      const newStatus = target.status === 'active' ? 'suspended' : 'active';

      const updated = await updateAdminUser(userId, { status: newStatus });
      setUsers(prevUsers => prevUsers.map(u => (String(u.id) === String(userId) ? updated : u)));
      setSuccess(`User status updated successfully`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to update user status');
      
      // Clear error message after 3 seconds
      setTimeout(() => {
        setError('');
      }, 3000);
    }
  };
  
  // Open edit modal
  const handleOpenEditModal = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      department: user.department,
      status: user.status
    });
    setShowEditModal(true);
  };
  
  // Handle edit form input change
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle edit form submit
  const handleEditSubmit = (e) => {
    e.preventDefault();
    
    (async () => {
      try {
        setError('');
        const updated = await updateAdminUser(selectedUser.id, editFormData);
        setUsers(prevUsers => prevUsers.map(u => (String(u.id) === String(selectedUser.id) ? updated : u)));
        setSuccess('User updated successfully');
        setShowEditModal(false);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      } catch (err) {
        console.error(err);
        setError(err?.message || 'Failed to update user');
        
        // Clear error message after 3 seconds
        setTimeout(() => {
          setError('');
        }, 3000);
      }
    })();
  };
  
  return (
    <>
      <div className="admin-page-content">
        <h2 className="mb-4">User Management</h2>
        
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <Card className="shadow-sm mb-4">
          <Card.Body>
            <h5 className="mb-3"><FaFilter className="me-2" /> Filters</h5>
                <Row>
                  <Col lg={4} md={6} className="mb-3">
                    <InputGroup>
                      <InputGroup.Text className="bg-light">
                        <FaSearch />
                      </InputGroup.Text>
                      <Form.Control 
                        type="text"
                        placeholder="Search by name, email, role..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border-start-0"
                      />
                    </InputGroup>
                  </Col>
                  <Col lg={2} md={6} sm={4} className="mb-3">
                    <Form.Select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="rounded-pill"
                      aria-label="Filter by role"
                    >
                      <option value="">All Roles</option>
                      <option value="buyer">Buyer</option>
                      <option value="seller">Seller</option>
                      <option value="admin">Admin</option>
                    </Form.Select>
                  </Col>
                  <Col lg={3} md={6} sm={4} className="mb-3">
                    <Form.Select
                      value={departmentFilter}
                      onChange={(e) => setDepartmentFilter(e.target.value)}
                      className="rounded-pill"
                      aria-label="Filter by department"
                    >
                      <option value="">All Departments</option>
                      {departments.map((dept, index) => (
                        <option key={index} value={dept}>{dept}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col lg={2} md={6} sm={4} className="mb-3">
                    <Form.Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="rounded-pill"
                      aria-label="Filter by status"
                    >
                      <option value="">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                    </Form.Select>
                  </Col>
                  <Col lg={1} className="mb-3 d-flex align-items-center">
                    <Button 
                      variant="outline-secondary"
                      className="rounded-pill w-100"
                      onClick={() => {
                        setSearchTerm('');
                        setRoleFilter('');
                        setDepartmentFilter('');
                        setStatusFilter('');
                      }}
                    >
                      Reset
                    </Button>
                  </Col>
                </Row>
            {loading ? (
              <div className="text-center py-5">
                <h4>Loading users...</h4>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-5">
                <h4>No users found</h4>
                <p className="text-muted">
                  Try adjusting your filters
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="align-middle">
                  <thead>
                    <tr>
                      <th 
                        onClick={() => requestSort('name')}
                        className="cursor-pointer"
                      >
                        Name {getSortIcon('name')}
                      </th>
                      <th 
                        onClick={() => requestSort('email')}
                        className="cursor-pointer"
                      >
                        Email {getSortIcon('email')}
                      </th>
                      <th 
                        onClick={() => requestSort('role')}
                        className="cursor-pointer"
                      >
                        Role {getSortIcon('role')}
                      </th>
                      <th 
                        onClick={() => requestSort('department')}
                        className="cursor-pointer"
                      >
                        Department {getSortIcon('department')}
                      </th>
                      <th 
                        onClick={() => requestSort('dateJoined')}
                        className="cursor-pointer"
                      >
                        Joined {getSortIcon('dateJoined')}
                      </th>
                      <th 
                        onClick={() => requestSort('status')}
                        className="cursor-pointer"
                      >
                        Status {getSortIcon('status')}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div 
                              className="avatar-circle me-2"
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: '#007bff',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold'
                              }}
                            >
                              {user.name && typeof user.name === 'string' ? user.name.charAt(0) : '?'}
                            </div>
                            <div className="fw-bold">{user.name || 'N/A'}</div>
                          </div>
                        </td>
                        <td>{user.email || 'N/A'}</td>
                        <td>
                          <Badge 
                            bg={
                              user.role === 'admin' ? 'danger' : 
                              user.role === 'seller' ? 'success' : 
                              'primary'
                            }
                          >
                            {user.role || 'N/A'}
                          </Badge>
                        </td>
                        <td>{user.department?.name || user.department?.title || user.department || 'N/A'}</td>
                        <td>{user.dateJoined ? new Date(user.dateJoined).toLocaleDateString() : 'N/A'}</td>
                        <td>
                          <Badge 
                            bg={user.status === 'active' ? 'success' : 'danger'}
                          >
                            {user.status || 'Unknown'}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex">
                            <Button 
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => handleOpenEditModal(user)}
                              title="Edit User"
                            >
                              <FaUserEdit />
                            </Button>
                            <Button 
                              variant={user.status === 'active' ? 'outline-danger' : 'outline-success'}
                              size="sm"
                              onClick={() => handleToggleStatus(user.id)}
                              title={user.status === 'active' ? 'Suspend User' : 'Activate User'}
                            >
                              {user.status === 'active' ? <FaUserSlash /> : <FaUserCheck />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
        
        {/* User Statistics */}
        <Row>
          <Col md={4} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Body>
                <h5 className="mb-3">User Statistics</h5>
                <div className="d-flex justify-content-between mb-2">
                  <span>Total Users:</span>
                  <span className="fw-bold">{users.length}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Active Users:</span>
                  <span className="fw-bold">
                    {users.filter(user => user.status === 'active').length}
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Suspended Users:</span>
                  <span className="fw-bold">
                    {users.filter(user => user.status === 'suspended').length}
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Buyers:</span>
                  <span className="fw-bold">
                    {users.filter(user => user.role === 'buyer').length}
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Sellers:</span>
                  <span className="fw-bold">
                    {users.filter(user => user.role === 'seller').length}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Admins:</span>
                  <span className="fw-bold">
                    {users.filter(user => user.role === 'admin').length}
                  </span>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={8} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Body>
                <h5 className="mb-3">Department Distribution</h5>
                <div className="table-responsive">
                  <Table>
                    <thead>
                      <tr>
                        <th>Department</th>
                        <th>Users</th>
                        <th>Buyers</th>
                        <th>Sellers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departments.map((department, index) => {
                        const departmentUsers = users.filter(user => user.department === department);
                        const buyers = departmentUsers.filter(user => user.role === 'buyer').length;
                        const sellers = departmentUsers.filter(user => user.role === 'seller').length;
                        
                        return (
                          <tr key={index}>
                            <td>{department}</td>
                            <td>{departmentUsers.length}</td>
                            <td>{buyers}</td>
                            <td>{sellers}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        {/* Edit User Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Edit User</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleEditSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditInputChange}
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={editFormData.email}
                  onChange={handleEditInputChange}
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Role</Form.Label>
                <Form.Select
                  name="role"
                  value={editFormData.role}
                  onChange={handleEditInputChange}
                  required
                >
                  <option value="buyer">Buyer</option>
                  <option value="seller">Seller</option>
                  <option value="admin">Admin</option>
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Department</Form.Label>
                <Form.Select
                  name="department"
                  value={editFormData.department}
                  onChange={handleEditInputChange}
                  required
                >
                  {(departments.length ? departments : [editFormData.department].filter(Boolean)).map((department, index) => (
                    <option key={index} value={department}>{department}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={editFormData.status}
                  onChange={handleEditInputChange}
                  required
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </Form.Select>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save Changes
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default AdminUsers;
